import { User, ApiResponse, type Region } from '@court-sync/shared';
import { apiGet, apiPost, apiPut, apiDelete } from './apiClient';

export interface UpdateProfileData {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  avatarUrl?: string | null;
}

export interface GenerateAiAvatarRequest {
  gender: 'female' | 'male' | 'non_binary';
  pose: 'forehand' | 'backhand' | 'serve' | 'ready_position';
  outfit: 'classic_white' | 'modern_blue' | 'vibrant_green' | 'sunset_orange';
  background: 'hard_court_day' | 'clay_court' | 'grass_court' | 'stadium_lights';
  mood: 'focused' | 'confident_smile' | 'calm';
}

export interface AiAvatarOptionsResponse {
  genders: Array<{ id: GenerateAiAvatarRequest['gender']; label: string }>;
  poses: Array<{ id: GenerateAiAvatarRequest['pose']; label: string }>;
  outfits: Array<{ id: GenerateAiAvatarRequest['outfit']; label: string }>;
  backgrounds: Array<{ id: GenerateAiAvatarRequest['background']; label: string }>;
  moods: Array<{ id: GenerateAiAvatarRequest['mood']; label: string }>;
}

export const userService = {
  async updateProfile(profileData: UpdateProfileData): Promise<ApiResponse<null>> {
    return apiPut<null>('/user/profile', profileData);
  },

  async getProfile(): Promise<ApiResponse<User>> {
    return apiGet<User>('/user/profile');
  },

  async updatePreferences(preferences: {
    theme?: 'light' | 'dark' | 'system';
    language?: string;
    tourCompletedAcademyOwnerDashboard?: boolean;
    academyOwnerSetupChecklistDismissed?: boolean;
    tourCompletedClubOwnerDashboard?: boolean;
    tourCompletedClubManagement?: boolean;
    tourCompletedProfessionalUpgrade?: boolean;
    tourCompletedEnterpriseUpgrade?: boolean;
    professionalUnlockBadgesActivatedAt?: string;
    enterpriseUnlockBadgesActivatedAt?: string;
    clubOwnerSetupChecklistDismissed?: boolean;
    clubOwnerSetupChecklistCompletedAt?: string;
    clubSetupSupportNudgeDismissed?: boolean;
    tourCompletedStudentDashboard?: boolean;
    tourCompletedAdminDashboard?: boolean;
    tourCompletedCampManagement?: boolean;
    tourCompletedTermManagement?: boolean;
    completedPageTours?: Record<string, boolean>;
    setupSupportNudgeDismissed?: boolean;
    selfCoachPromptDismissed?: Record<string, boolean>;
    notifications?: {
      email?: boolean;
      push?: boolean;
      sms?: boolean;
    };
  }): Promise<ApiResponse<null>> {
    return apiPut<null>('/user/preferences', preferences);
  },

  async getPreferences(): Promise<ApiResponse<unknown>> {
    return apiGet<unknown>('/user/preferences');
  },

  async updateRegion(region: Region): Promise<ApiResponse<{ region: Region }>> {
    return apiPut<{ region: Region }>('/user/region', { region });
  },

  async deleteAccount(): Promise<ApiResponse<null>> {
    return apiDelete<null>('/user/profile');
  },

  async getAiAvatarOptions(): Promise<ApiResponse<AiAvatarOptionsResponse>> {
    return apiGet<AiAvatarOptionsResponse>('/user/avatar/ai-options');
  },

  async generateAiAvatar(payload: GenerateAiAvatarRequest): Promise<ApiResponse<{ avatarUrl: string; rateLimit: { remaining: number; limit: number; windowMs: number } }>> {
    return apiPost<{ avatarUrl: string; rateLimit: { remaining: number; limit: number; windowMs: number } }>(
      '/user/avatar/generate',
      payload
    );
  },
};
